/**
 * CUSTOM: Product tabs
 * Added: 2026-10-08
 * Description: Tab switching and the mobile pill strip for sections/custom-product-tabs.liquid
 */
import { Component } from '@theme/component';

/**
 * @typedef {Object} ProductTabsRefs
 * @property {HTMLElement} strip
 * @property {HTMLButtonElement} [prev]
 * @property {HTMLButtonElement} [next]
 * @property {HTMLButtonElement[]} [tabs]
 * @property {HTMLElement[]} [panels]
 */

const END_TOLERANCE = 4; // Px of slack before the strip counts as scrolled to the end

/**
 * Tab list on the left, the active panel on the right. On mobile the tab list
 * is a swipeable strip with an edge fade and a "more" arrow.
 * Tab mechanics follow layered-slideshow.js: roving tabindex, Arrow/Home/End keys, inert panels.
 *
 * @extends {Component<ProductTabsRefs>}
 */
class ProductTabsComponent extends Component {
  requiredRefs = ['strip'];
  #active = 0;
  /** @type {AbortController | null} */
  #abort = null;
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  connectedCallback() {
    super.connectedCallback();

    const tabs = this.refs.tabs ?? [];
    this.#active = Math.max(
      0,
      tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true')
    );

    this.#abort = new AbortController();
    const opts = { signal: this.#abort.signal };
    const { strip, prev, next } = this.refs;

    this.addEventListener('click', this.#handleClick, opts);
    this.addEventListener('keydown', this.#handleKeydown, opts);
    strip.addEventListener('scroll', this.#updateStripState, { ...opts, passive: true });
    prev?.addEventListener('click', this.#scrollStripBack, opts);
    next?.addEventListener('click', this.#scrollStripForward, opts);

    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:block:select', this.#handleBlockSelect, opts);
    }

    this.#resizeObserver = new ResizeObserver(this.#updateStripState);
    this.#resizeObserver.observe(strip);
    this.#updateStripState();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
  }

  /**
   * Public method to select a tab by index
   * @param {number} index
   */
  select(index) {
    this.#activate(index);
  }

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    const tab = /** @type {HTMLElement} */ (event.target).closest('[role="tab"]');
    if (!tab) return;

    const index = (this.refs.tabs ?? []).indexOf(/** @type {HTMLButtonElement} */ (tab));
    if (index !== -1) this.#activate(index);
  };

  /** @param {KeyboardEvent} event */
  #handleKeydown = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    if (target.getAttribute('role') !== 'tab') return;

    const tabs = this.refs.tabs ?? [];
    const i = tabs.indexOf(/** @type {HTMLButtonElement} */ (target));
    if (i === -1) return;

    const navMap = {
      ArrowLeft: -1,
      ArrowUp: -1,
      ArrowRight: 1,
      ArrowDown: 1,
      Home: -i,
      End: tabs.length - 1 - i,
    };

    const offset = navMap[/** @type {keyof typeof navMap} */ (event.key)];
    if (offset === undefined) return;

    event.preventDefault();
    const nextIndex = (i + offset + tabs.length) % tabs.length;
    this.#activate(nextIndex);
    tabs[nextIndex]?.focus({ preventScroll: true });
  };

  /** Opens the tab holding the block the merchant selected in the theme editor. */
  #handleBlockSelect = (/** @type {Event} */ event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    if (!this.contains(target)) return;

    const panel = target.closest('[role="tabpanel"]');
    if (!panel) return;

    const index = (this.refs.tabs ?? []).findIndex((tab) => tab.getAttribute('aria-controls') === panel.id);
    if (index !== -1) this.#activate(index, true);
  };

  /**
   * @param {number} index
   * @param {boolean} [force] Re-apply state even when the index is unchanged (editor re-renders)
   */
  #activate(index, force = false) {
    const tabs = this.refs.tabs ?? [];
    if (index < 0 || index >= tabs.length) return;
    if (index === this.#active && !force) return;

    this.#active = index;

    for (const [i, tab] of tabs.entries()) {
      const isActive = i === index;
      tab.setAttribute('aria-selected', String(isActive));
      tab.setAttribute('tabindex', isActive ? '0' : '-1');

      const panel = this.#panelFor(tab);
      if (!panel) continue;
      // Removing `hidden` restarts the panel's CSS entrance animation.
      panel.hidden = !isActive;
      panel.inert = !isActive;
    }

    this.#scrollTabIntoView(tabs[index]);
  }

  /**
   * @param {HTMLElement} tab
   * @returns {HTMLElement | null}
   */
  #panelFor(tab) {
    const id = tab.getAttribute('aria-controls');
    return (this.refs.panels ?? []).find((panel) => panel.id === id) ?? null;
  }

  /**
   * Keeps the active pill visible in the mobile strip. Uses the strip's own
   * scrollTo so the page (Horizon's .page-wrapper scroller) never moves.
   * @param {HTMLElement | undefined} tab
   */
  #scrollTabIntoView(tab) {
    const { strip } = this.refs;
    if (!tab || strip.scrollWidth <= strip.clientWidth) return;

    const stripRect = strip.getBoundingClientRect();
    const tabRect = tab.getBoundingClientRect();
    const padding = parseFloat(getComputedStyle(strip).paddingInlineStart) || 0;

    if (tabRect.left < stripRect.left + padding) {
      strip.scrollTo({ left: strip.scrollLeft + tabRect.left - stripRect.left - padding, behavior: 'smooth' });
    } else if (tabRect.right > stripRect.right - padding) {
      strip.scrollTo({ left: strip.scrollLeft + tabRect.right - stripRect.right + padding, behavior: 'smooth' });
    }
  }

  #scrollStripBack = () => {
    const { strip } = this.refs;
    strip.scrollBy({ left: -strip.clientWidth * 0.6, behavior: 'smooth' });
  };

  #scrollStripForward = () => {
    const { strip } = this.refs;
    strip.scrollBy({ left: strip.clientWidth * 0.6, behavior: 'smooth' });
  };

  /** Toggles the edge fades and arrows depending on whether tabs sit off-screen on either side. */
  #updateStripState = () => {
    const { strip, prev, next } = this.refs;
    const overflows = strip.scrollWidth > strip.clientWidth + END_TOLERANCE;
    const atStart = !overflows || strip.scrollLeft <= END_TOLERANCE;
    const atEnd = !overflows || strip.scrollLeft + strip.clientWidth >= strip.scrollWidth - END_TOLERANCE;

    strip.toggleAttribute('data-at-start', atStart);
    strip.toggleAttribute('data-at-end', atEnd);
    if (prev) prev.hidden = atStart;
    if (next) next.hidden = atEnd;
  };
}

if (!customElements.get('custom-product-tabs-component')) {
  customElements.define('custom-product-tabs-component', ProductTabsComponent);
}
